import { useEffect, useRef, type KeyboardEvent } from 'react';

import type { CapturedRequest } from '../lib/api.js';
import { methodTone, relativeTime, statusTone } from '../lib/format.js';

export interface RequestListProps {
  requests: CapturedRequest[];
  selectedId: string | undefined;
  onSelect: (request: CapturedRequest) => void;
  /** Ticks once a second so "hace 3 s" stays true without re-fetching. */
  now: number;
}

/**
 * The captured requests, newest first.
 *
 * A real `listbox` rather than a list of buttons: that is what gives arrow-key
 * navigation for free in screen readers, and it is the difference between the
 * keyboard support being announced and merely existing. Roving `tabIndex` keeps
 * the whole list a single tab stop.
 */
export function RequestList({ requests, selectedId, onSelect, now }: RequestListProps) {
  const listRef = useRef<HTMLUListElement>(null);
  const selectedIndex = requests.findIndex((request) => request.id === selectedId);

  // Follow the selection when it moves by keyboard, but never yank the view
  // when a new request arrives at the top while the user is reading further down.
  useEffect(() => {
    if (selectedIndex < 0) return;
    const option = listRef.current?.querySelector<HTMLElement>(`[data-index="${selectedIndex}"]`);
    option?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>): void => {
    if (requests.length === 0) return;

    const move = (to: number): void => {
      event.preventDefault();
      const clamped = Math.min(Math.max(to, 0), requests.length - 1);
      const next = requests[clamped];
      if (next) onSelect(next);
    };

    switch (event.key) {
      case 'ArrowDown':
        return move(selectedIndex + 1);
      case 'ArrowUp':
        // From nothing selected, Up should land on the newest, not the oldest.
        return move(selectedIndex <= 0 ? 0 : selectedIndex - 1);
      case 'Home':
        return move(0);
      case 'End':
        return move(requests.length - 1);
      case 'PageDown':
        return move(selectedIndex + 10);
      case 'PageUp':
        return move(Math.max(selectedIndex - 10, 0));
      default:
    }
  };

  return (
    <ul
      ref={listRef}
      role="listbox"
      aria-label="Requests capturados"
      tabIndex={0}
      onKeyDown={onKeyDown}
      className="divide-y divide-slate-800/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-400"
    >
      {requests.map((request, index) => {
        const selected = request.id === selectedId;

        return (
          <li
            key={request.id}
            role="option"
            aria-selected={selected}
            data-index={index}
            data-testid="request-row"
            onClick={() => onSelect(request)}
            className={`cursor-pointer px-3 py-2.5 transition ${
              selected ? 'bg-sky-500/10 ring-1 ring-inset ring-sky-500/40' : 'hover:bg-slate-800/50'
            }`}
          >
            <div className="flex items-center gap-2">
              <span
                className={`rounded px-1.5 py-0.5 font-mono text-[11px] font-semibold ${methodTone(request.method)}`}
              >
                {request.method}
              </span>
              <span className="min-w-0 flex-1 truncate font-mono text-sm text-slate-200">
                {request.path}
              </span>
              <span
                className={`rounded px-1.5 py-0.5 font-mono text-[11px] ring-1 ${statusTone(request.statusReturned)}`}
              >
                {request.statusReturned}
              </span>
            </div>

            <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500">
              <time dateTime={request.createdAt}>{relativeTime(request.createdAt, now)}</time>
              <span aria-hidden="true">·</span>
              <span>{request.durationMs} ms</span>
              {request.bodyTruncated && (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="text-amber-400">truncado</span>
                </>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
